import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { User } from '../core/models/models';
import * as AuthActions from './actions/auth.actions';
import { AuthState } from './reducers/auth.reducer';
import * as AuthReducers from './reducers/auth.reducer';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthService } from '@auth0/auth0-angular';
import { map, switchMap } from 'rxjs/operators';
import { externalDataAssign } from '../integrations/models/external-data-integration.model';
import { PlatformIntegration } from '../integrations/models/platform-integration.enum';
import { PlatformSesionSelector } from '../integrations/models/platform-sesion-selector.enum';
import { environment } from 'src/environments/environment';
import { Config } from 'src/environments/config.model';

@Injectable({ providedIn: 'root' })
export class AuthFactoryService {
  private platform: PlatformIntegration;
  env: Config = environment;

  constructor(
    private store: Store<AuthState>,
    public oidcSecurityService: OidcSecurityService,
    public auth0Service: AuthService
  ) {
    this.platform = this.parseCurrentPlatform(
      window.localStorage.getItem(PlatformSesionSelector.currentPlatform)
    );
  }

  clearExternalDataAssign() {
    window.localStorage.removeItem(externalDataAssign);
  }

  parseCurrentPlatform(lsPlatform: string): PlatformIntegration {
    if (lsPlatform) {
      switch (lsPlatform) {
        case PlatformIntegration.default:
          return PlatformIntegration.default;

        case PlatformIntegration.salesforce:
          return PlatformIntegration.salesforce;
      }
    } else {
      return PlatformIntegration.default;
    }
  }

  /**
   * Methods dependent from platform
   * *Below methods has to be configured according the current platform
   */

  getAccessToken(): Observable<string> {
    switch (this.platform) {
      case PlatformIntegration.default:
        return this.oidcSecurityService.getAccessToken();

      case PlatformIntegration.salesforce:
        return this.auth0Service.getAccessTokenSilently({ cacheMode: 'cache-only' });
    }
  }

  getSesionStatusToken(): Observable<[boolean, string]> {
    switch (this.platform) {
      case PlatformIntegration.default:
        return this.oidcSecurityService
          .checkAuth()
          .pipe(map(({ isAuthenticated, accessToken }) => [isAuthenticated, accessToken]));

      case PlatformIntegration.salesforce:
        let auth0StatusToken: Observable<[boolean, string]> = new Observable();

        return this.auth0Service.isAuthenticated$.pipe(
          switchMap((isAuth) => {
            if (isAuth) {
              auth0StatusToken = combineLatest([
                of(true),
                this.auth0Service.getAccessTokenSilently()
              ]);
            } else {
              auth0StatusToken = of([false, null]);
            }

            return auth0StatusToken;
          })
        );
    }
  }

  redirectToLogin() {
    this.clearExternalDataAssign();

    switch (this.platform) {
      case PlatformIntegration.default:
        this.oidcSecurityService.authorize();
        break;

      case PlatformIntegration.salesforce:
        this.auth0Service.loginWithRedirect();
        break;
    }
  }

  logoff() {
    this.clearExternalDataAssign();

    switch (this.platform) {
      case PlatformIntegration.default:
        this.oidcSecurityService.logoff();
        break;

      case PlatformIntegration.salesforce:
        this.auth0Service.logout({
          logoutParams: {
            federated: this.env.auth0UFederated,
            returnTo: this.env.auth0UReturnTo
          }
        });
        break;
    }
  }

  forceRefreshSession(): Observable<LoginResponse | string> {
    switch (this.platform) {
      case PlatformIntegration.default:
        return this.oidcSecurityService.forceRefreshSession();

      case PlatformIntegration.salesforce:
        return this.auth0Service.getAccessTokenSilently();
    }
  }

  /**
   * END Methods dependent from platform
   */

  /**
   * Methods independent from platform
   * *Below methods are independent of the current platform
   */

  getCurrentPlatform(): PlatformIntegration {
    return this.platform;
  }

  getUser(): User {
    const user: AuthState = JSON.parse(localStorage.getItem('auth'));
    return user.user;
  }

  getLang(): Observable<string> {
    return this.store.select(AuthReducers.selectLang);
  }

  getOpenSignIn(): Observable<boolean> {
    return this.store.pipe(select(AuthReducers.selectOpenSignin));
  }

  getEndpoint(): Observable<string> {
    return this.store.pipe(select(AuthReducers.selectEndoint));
  }

  saveUser(user: User) {
    this.store.dispatch(AuthActions.SaveUser({ user }));
  }

  dispatchRefreshToken(openSignin: boolean) {
    this.store.dispatch(AuthActions.RefreshToken({ openSignin }));
  }

  dispatchSaveToken(token: string) {
    this.store.dispatch(AuthActions.SaveToken({ token }));
  }

  dispatchSaveLang(lang: string) {
    this.store.dispatch(AuthActions.SaveLang({ lang }));
  }

  dispatchUserInfoSuccess(user: User) {
    this.store.dispatch(AuthActions.LoadUserInfoSuccess({ user }));
  }

  dispatchInvalidToken() {
    this.store.dispatch(AuthActions.InvalidToken());
  }

  dispatchGetAuthEndpoint() {
    this.store.dispatch(AuthActions.GetEndpoint());
  }

  /**
   * END Methods independent from platform
   */
}
